/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  ComunicazioneDTO,
  ComunicazioneFrontOfficeUpdateRequest,
  GetFrontComunicazioniParams,
  PostFrontComunicazioniIdTipiAllegatoIdAllegatiBody,
  WsRispostaPaginataListComunicazioneDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ricerca puntuale istanza comunicazione
 * @summary Ricerca puntuale istanza comunicazione
 */
export const getFrontComunicazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ComunicazioneDTO>(
    {
      url: `/framework-cmp-domande/comunicazioni/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontComunicazioniIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/comunicazioni/${id}`] as const;

export const getGetFrontComunicazioniIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontComunicazioniId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontComunicazioniIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontComunicazioniId>>
  > = ({ signal }) => getFrontComunicazioniId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontComunicazioniIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontComunicazioniId>>
>;
export type GetFrontComunicazioniIdQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca puntuale istanza comunicazione
 */
export const useGetFrontComunicazioniId = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontComunicazioniIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica una Comunicazione
 * @summary Modifica una Comunicazione
 */
export const putFrontComunicazioniId = (
  id: string,
  comunicazioneFrontOfficeUpdateRequest: ComunicazioneFrontOfficeUpdateRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/comunicazioni/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: comunicazioneFrontOfficeUpdateRequest,
    },
    options,
  );
};

export const getPutFrontComunicazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontComunicazioniId>>,
    TError,
    { id: string; data: ComunicazioneFrontOfficeUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontComunicazioniId>>,
  TError,
  { id: string; data: ComunicazioneFrontOfficeUpdateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontComunicazioniId>>,
    { id: string; data: ComunicazioneFrontOfficeUpdateRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putFrontComunicazioniId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontComunicazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putFrontComunicazioniId>>
>;
export type PutFrontComunicazioniIdMutationBody =
  ComunicazioneFrontOfficeUpdateRequest;
export type PutFrontComunicazioniIdMutationError = ErrorType<unknown>;

/**
 * @summary Modifica una Comunicazione
 */
export const usePutFrontComunicazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontComunicazioniId>>,
    TError,
    { id: string; data: ComunicazioneFrontOfficeUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutFrontComunicazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancella una Comunicazione
 * @summary Cancella una Comunicazione
 */
export const deleteFrontComunicazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cmp-domande/comunicazioni/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteFrontComunicazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontComunicazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFrontComunicazioniId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFrontComunicazioniId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteFrontComunicazioniId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFrontComunicazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFrontComunicazioniId>>
>;

export type DeleteFrontComunicazioniIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella una Comunicazione
 */
export const useDeleteFrontComunicazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontComunicazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteFrontComunicazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea un allegato e lo associa a una comunicazione
 * @summary Crea un allegato e lo associa a una comunicazione
 */
export const postFrontComunicazioniIdTipiAllegatoIdAllegati = (
  id: string,
  idTipoAllegato: string,
  postFrontComunicazioniIdTipiAllegatoIdAllegatiBody: PostFrontComunicazioniIdTipiAllegatoIdAllegatiBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append(
    'file',
    postFrontComunicazioniIdTipiAllegatoIdAllegatiBody.file,
  );

  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/comunicazioni/${id}/tipi-allegato/${idTipoAllegato}/allegati`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getPostFrontComunicazioniIdTipiAllegatoIdAllegatiMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof postFrontComunicazioniIdTipiAllegatoIdAllegati>
      >,
      TError,
      {
        id: string;
        idTipoAllegato: string;
        data: PostFrontComunicazioniIdTipiAllegatoIdAllegatiBody;
      },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof postFrontComunicazioniIdTipiAllegatoIdAllegati>>,
    TError,
    {
      id: string;
      idTipoAllegato: string;
      data: PostFrontComunicazioniIdTipiAllegatoIdAllegatiBody;
    },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof postFrontComunicazioniIdTipiAllegatoIdAllegati>
      >,
      {
        id: string;
        idTipoAllegato: string;
        data: PostFrontComunicazioniIdTipiAllegatoIdAllegatiBody;
      }
    > = props => {
      const { id, idTipoAllegato, data } = props ?? {};

      return postFrontComunicazioniIdTipiAllegatoIdAllegati(
        id,
        idTipoAllegato,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostFrontComunicazioniIdTipiAllegatoIdAllegatiMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontComunicazioniIdTipiAllegatoIdAllegati>>
  >;
export type PostFrontComunicazioniIdTipiAllegatoIdAllegatiMutationBody =
  PostFrontComunicazioniIdTipiAllegatoIdAllegatiBody;
export type PostFrontComunicazioniIdTipiAllegatoIdAllegatiMutationError =
  ErrorType<unknown>;

/**
 * @summary Crea un allegato e lo associa a una comunicazione
 */
export const usePostFrontComunicazioniIdTipiAllegatoIdAllegati = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontComunicazioniIdTipiAllegatoIdAllegati>>,
    TError,
    {
      id: string;
      idTipoAllegato: string;
      data: PostFrontComunicazioniIdTipiAllegatoIdAllegatiBody;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontComunicazioniIdTipiAllegatoIdAllegatiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Invia la comunicazione e la protocolla se il tipoComunicazione associato lo prevede
 * @summary Invia la comunicazione e la protocolla se il tipoComunicazione associato lo prevede
 */
export const postFrontComunicazioniInviaComunicazioneId = (
  idIstanzaComunicazione: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/comunicazioni/inviaComunicazione/${idIstanzaComunicazione}`,
      method: 'post',
    },
    options,
  );
};

export const getPostFrontComunicazioniInviaComunicazioneIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontComunicazioniInviaComunicazioneId>>,
    TError,
    { idIstanzaComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontComunicazioniInviaComunicazioneId>>,
  TError,
  { idIstanzaComunicazione: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontComunicazioniInviaComunicazioneId>>,
    { idIstanzaComunicazione: string }
  > = props => {
    const { idIstanzaComunicazione } = props ?? {};

    return postFrontComunicazioniInviaComunicazioneId(
      idIstanzaComunicazione,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontComunicazioniInviaComunicazioneIdMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontComunicazioniInviaComunicazioneId>>
  >;

export type PostFrontComunicazioniInviaComunicazioneIdMutationError =
  ErrorType<unknown>;

/**
 * @summary Invia la comunicazione e la protocolla se il tipoComunicazione associato lo prevede
 */
export const usePostFrontComunicazioniInviaComunicazioneId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontComunicazioniInviaComunicazioneId>>,
    TError,
    { idIstanzaComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontComunicazioniInviaComunicazioneIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ricerca istanza comunicazioni inviate o ricevute dall'utente frontoffice loggato
 * @deprecated
 * @summary Ricerca istanza comunicazioni inviate o ricevute dall'utente frontoffice loggato
 */
export const getFrontComunicazioni = (
  params?: GetFrontComunicazioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListComunicazioneDTO>(
    {
      url: `/framework-cmp-domande/comunicazioni`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontComunicazioniQueryKey = (
  params?: GetFrontComunicazioniParams,
) =>
  [
    `/framework-cmp-domande/comunicazioni`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontComunicazioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontComunicazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontComunicazioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontComunicazioniQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontComunicazioni>>
  > = ({ signal }) => getFrontComunicazioni(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontComunicazioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontComunicazioni>>
>;
export type GetFrontComunicazioniQueryError = ErrorType<unknown>;

/**
 * @deprecated
 * @summary Ricerca istanza comunicazioni inviate o ricevute dall'utente frontoffice loggato
 */
export const useGetFrontComunicazioni = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontComunicazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontComunicazioniQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Scarica allegato della comunicazione
 * @summary Scarica allegato della comunicazione
 */
export const getFrontComunicazioniAllegatiId = (
  idAllegato: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cmp-domande/comunicazioni/allegati/${idAllegato}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontComunicazioniAllegatiIdQueryKey = (
  idAllegato: string,
) => [`/framework-cmp-domande/comunicazioni/allegati/${idAllegato}`] as const;

export const getGetFrontComunicazioniAllegatiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
  TError = ErrorType<unknown>,
>(
  idAllegato: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontComunicazioniAllegatiIdQueryKey(idAllegato);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>
  > = ({ signal }) =>
    getFrontComunicazioniAllegatiId(idAllegato, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idAllegato, ...queryOptions };
};

export type GetFrontComunicazioniAllegatiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>
>;
export type GetFrontComunicazioniAllegatiIdQueryError = ErrorType<unknown>;

/**
 * @summary Scarica allegato della comunicazione
 */
export const useGetFrontComunicazioniAllegatiId = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
  TError = ErrorType<unknown>,
>(
  idAllegato: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontComunicazioniAllegatiIdQueryOptions(
    idAllegato,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cancellazione allegato comunicazione
 * @summary Cancellazione allegato comunicazione
 */
export const deleteFrontComunicazioniAllegatiId = (
  idAllegato: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/comunicazioni/allegati/${idAllegato}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteFrontComunicazioniAllegatiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontComunicazioniAllegatiId>>,
    TError,
    { idAllegato: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFrontComunicazioniAllegatiId>>,
  TError,
  { idAllegato: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFrontComunicazioniAllegatiId>>,
    { idAllegato: string }
  > = props => {
    const { idAllegato } = props ?? {};

    return deleteFrontComunicazioniAllegatiId(idAllegato, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFrontComunicazioniAllegatiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFrontComunicazioniAllegatiId>>
>;

export type DeleteFrontComunicazioniAllegatiIdMutationError =
  ErrorType<unknown>;

/**
 * @summary Cancellazione allegato comunicazione
 */
export const useDeleteFrontComunicazioniAllegatiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontComunicazioniAllegatiId>>,
    TError,
    { idAllegato: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteFrontComunicazioniAllegatiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
